const UtfGridOverlay = require("./utfgridoverlay");
const SearchParameters = require("../searchparameters").SearchParameters;
const maps = require("../maps");

/**
 * TiledImageLayerOptions
 * @typedef {Object} TiledImageLayerOptions
 * @property {google.maps.Map} [map] the map where the layer will be displayed.
 * @property {TileStyle} [style] object that contains style parameters for the current layer.
 * @property {Number} [maxZoom] the layer will be visible between 0 and `maxZoom`.
 * @property {woosmap.search.SearchParameters} searchParameters
 * @since 1.1.0
 */

/**
 * TileStyle Object specifications.
 * @typedef {Object} TileStyle
 * @property {String} [color] a color rgba formatted
 * @property {Number} [size] the size of the marker in pixels, between 1 and 16.
 * @property {Number} [minSize] the minimum size acceptable for the marker.
 * @property {Number} updatedAt the timestamp at which the data was updated.
 * @property {Array.<TypesRules>} [typeRules] rules to display different color depending of types.
 * Two different rules cannot be applied to the same store.
 * In case of conflict the latest type in the array win.
 * If a store has no rule, the default type is apply.
 * @since 1.1.0
 */

/**
 * TypesRules Object specifications.
 * @typedef {Object} TypesRules
 * @property {String} type of a store
 * @property {String} color a color rgba formatted
 * @property {Number} [zIndex] the zIndex priority of the points
 * @since 1.2.0
 */

/**
 * @memberOf woosmap.layers
 * @since 1.1.0
 */
class TiledImageLayer {
    /**
     * Constructs a new TiledImageLayer
     * @param {TiledImageLayerOptions} [options] options including map, and style.
     * @constructor
     */
    constructor(options) {
        if (!options.updatedAt) {
            throw new Error("TiledImageLayer: updatedAt option is mandatory.");
        }
        this._updatedAt = options.updatedAt;

        this._tiler_url = maps.utils.overlays.getTilesUrlTemplate(this._updatedAt);

        this.selectedStore = null;

        /**
         * The style to apply to the image layer.
         * @type {TileStyle}
         * @private
         */
        this._style = null;
        this._searchParameters = options.searchParameters || new SearchParameters();
        this._utfGrid = null;

        this._map = null;

        this._storeEventManager = options.eventManager;

        if (options) {
            if (options.map) {
                this.setMap(options.map);
            }
            if (options.style) {
                this._style = options.style;
            }
            this.maxZoom = options.maxZoom;
        }

        this.maxZoom = this.maxZoom || 24;
        this._initEncodedTypeRules();
        this._imageType = null;
    }

    /**
     * Initialize _encodedRules for tiles url request
     * @private
     */
    _initEncodedTypeRules() {
        if (this._style?.typeRules) {
            if (this._style.typeRules.length <= 5) {
                this._tiler_url += `&typeRules=${encodeURIComponent(JSON.stringify(this._style.typeRules))}`;
            } else if (!(typeof console === "undefined" || typeof console.error === "undefined")) {
                console.error("Rules for store types should be equals or less than five.");
            }
        }
    }

    /**
     * Set an overlay to a map with the tiles generated by the tiler server
     * @param {google.maps.Map}map the map to set the overlay
     * FIXME: Not sure if it's the best way of doing this.
     */
    setMap(map) {
        if (this._map) {
            //maps.utils.overlays.removeOverlayAt(this._map, this._imageType, 0);

            this._utfGrid.setMap(null);
        }
        if (map) {
            this._map = map;
            //this._imageType = this._getImageType();
            //maps.utils.overlays.addImageOverlay(this._map, this._imageType, 0);
            this._initHitGrid();
        } else {
            this._map = null;
        }
    }

    /**
     * Returns the google map bound to the image layer.
     * @returns {google.maps.Map|null}
     */
    getMap() {
        return this._map;
    }

    /**
     * Sets the search parameters used to filter the displayed data.
     * @param {woosmap.search.SearchParameters} searchParameters
     */
    setSearchParameters(searchParameters) {
        this._searchParameters = searchParameters;
        this._refreshMap();
    }

    /**
     * Sets the searchQuery used to filter the displayed data.
     * @param {woosmap.search.SearchQuery}searchQuery the searchQuery.
     * @deprecated
     */
    setSearchQuery(searchQuery) {
        this._searchParameters.searchQuery = searchQuery;
        this._refreshMap();
    }

    /**
     * Sets the polylineFilter used to filter the displayed data.
     * @param {PolylineFilter}polylineFilter
     * @deprecated
     */
    setPolylineFilter(polylineFilter) {
        this._searchParameters.polylineFilter = polylineFilter;
        this._refreshMap();
    }

    /**
     * Initialize grid map and set selectedStore properties when user click on store in map
     * @private
     */
    _initHitGrid() {
        if (this._utfGrid) {
            this._utfGrid.setMap(null);
        }

        this._utfGrid = new UtfGridOverlay(this._buildTilesUrlTemplate(), {
            maxZoom: this.maxZoom,
            storeEventManager: this._storeEventManager,
        });
        this._utfGrid.setMap(this._map);
    }

    /**
     * Removes the OverlayMapType and the UtfGridOverlay
     * @private
     */
    _refreshMap() {
        return;
        if (this._map) {
            maps.utils.overlays.removeOverlayAt(this._map, this._imageType, 0);
            this._imageType = this._getImageType();
            maps.utils.overlays.addImageOverlay(this._map, this._imageType, 0);
            this._initHitGrid();
        }
    }

    /**
     * Create and return an ImageMapType
     * @private
     * @returns {google.maps.ImageMapType} a google image type
     * (https://developers.google.com/maps/documentation/javascript/3.exp/reference#ImageMapType)
     */
    _getImageType() {
        return new google.maps.ImageMapType({
            url: this._buildTilesUrlTemplate(null, null).replace("$X", "{x}").replace("$Y", "{y}").replace("$Z", "{z}"),
            tileSize: new google.maps.Size(256, 256),
        });

        return maps.utils.overlays.getImageOverlay((coord, zoom) => {
            return this._getTileUrl(coord, zoom, this._style);
        });
    }

    /**
     * Normalize coordinates to avoid errors when using y-continuous world.
     * @param coord
     * @param zoom
     * @returns {{x:Number, y:Number}}
     * @private
     */
    _normalizeCoordinates(coord, zoom) {
        const y = coord.y;
        let x = coord.x;

        // tile range in one direction range is dependent on zoom level
        // 0 = 1 tile, 1 = 2 tiles, 2 = 4 tiles, 3 = 8 tiles, etc
        const tileRange = 1 << zoom;

        // don't repeat across y-axis (vertically)
        if (y < 0 || y >= tileRange) {
            return null;
        }

        // repeat across x-axis
        if (x < 0 || x >= tileRange) {
            x = ((x % tileRange) + tileRange) % tileRange;
        }

        return { x: x, y: y };
    }

    /**
     * Computes the tiled marker radius a linear interpolation is done on the size.
     * @param style
     * @param zoom
     * @returns {Number}
     * @private
     */
    _computeSizeFromZoom(style, zoom) {
        let size = style.size;
        if (zoom !== undefined) {
            const minSize = style.minSize;
            const zoomDelta = this.maxZoom - Number.parseInt(zoom);
            if (zoomDelta > 0) {
                size = minSize + Math.floor((style.size - minSize) / zoomDelta);
            }
        }
        return size;
    }

    _getTileUrl(coord, zoom, style) {
        const normalizedCoordinates = this._normalizeCoordinates(coord, zoom);

        if (normalizedCoordinates && zoom <= this.maxZoom) {
            return this._buildTilesUrlTemplate(style, zoom)
                .replace(/\$Z/g, zoom)
                .replace(/\$Y/g, normalizedCoordinates.y)
                .replace(/\$X/g, normalizedCoordinates.x);
        }
        return null;
    }

    /**
     * Build and return the tiler url from style options
     * @private
     * @returns {String} a tiles url
     */
    _buildTilesUrlTemplate(style, zoom) {
        let url = this._tiler_url;
        if (style) {
            if (style.size) {
                let size = style.size;
                if (style.minSize) {
                    size = this._computeSizeFromZoom(style, zoom);
                }
                url += `&size=${encodeURIComponent(String(size))}`;
            }
            if (style.color) {
                url += `&color=${encodeURIComponent(style.color)}`;
            }
        }
        url += "&retina=yes";
        url += this._searchParameters.buildUrl();

        return url;
    }
}

module.exports = TiledImageLayer;
